import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import PendingActionsIcon from '@mui/icons-material/PendingActionsOutlined';
import { apiClient } from '@/api/axiosClient';
import StatCard from '@/components/common/StatCard';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardLineChart from '@/components/dashboard/DashboardLineChart';
import DashboardPieChart from '@/components/dashboard/DashboardPieChart';
import { filterLastNMonths, groupCountBy, groupSumByMonth } from '@/lib/dashboardAggregations';

const TREND_MONTHS = 6;
import { usePortalCategoryColors } from '../portalColors';
import { isWithinDateRange } from '@/lib/dateRangeFilter';
import PortalDateRangeFilter from '../PortalDateRangeFilter';
import type { InvoiceDto } from '@/types/billing';

const STATUS_SLICE_COLORS = ['#bec6e0', '#0566d9', '#ffb951', '#4edea3', '#ba1a1a'];
const formatCurrency = (v: number) => `₹${v.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

async function downloadInvoicePdf(invoice: InvoiceDto) {
  const response = await apiClient.get(`/client-portal/invoices/${invoice.id}/pdf`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(response.data as Blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${invoice.invoiceNumber}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function InvoicesPanel() {
  const { t } = useTranslation('clientPortal');
  const colors = usePortalCategoryColors();
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['client-portal', 'invoices'],
    queryFn: async () => (await apiClient.get<InvoiceDto[]>('/client-portal/invoices')).data,
  });
  // Date-range filter only narrows the table below — the stat cards/trend chart above keep
  // summarizing the full history (the chart already has its own 6-month window).
  const tableRows = useMemo(() => rows.filter((r) => isWithinDateRange(r.invoiceDate, fromDate, toDate)), [rows, fromDate, toDate]);

  const totalInvoiced = useMemo(() => rows.reduce((sum, r) => sum + r.totalAmount, 0), [rows]);
  const paidAmount = useMemo(() => rows.filter((r) => r.status === 'Paid').reduce((sum, r) => sum + r.totalAmount, 0), [rows]);
  const unpaidAmount = useMemo(
    () => rows.filter((r) => r.status === 'Finalized' || r.status === 'Sent').reduce((sum, r) => sum + r.totalAmount, 0),
    [rows],
  );

  const statusDistribution = useMemo(() => groupCountBy(rows, (r) => r.status), [rows]);
  const monthlyBilled = useMemo(
    () =>
      groupSumByMonth(
        filterLastNMonths(rows.filter((r) => r.status !== 'Draft'), (r) => r.invoiceDate, TREND_MONTHS),
        (r) => r.invoiceDate,
        [{ key: 'amount', value: (r) => r.totalAmount }],
      ),
    [rows],
  );

  const columns: DataTableColumn<InvoiceDto>[] = [
    { field: 'invoiceNumber', headerName: t('invoicesPage.table.invoiceNumber') },
    { field: 'invoiceDate', headerName: t('invoicesPage.table.invoiceDate'), render: (r) => new Date(r.invoiceDate).toLocaleDateString() },
    {
      field: 'billingPeriodStart',
      headerName: t('invoicesPage.table.billingPeriod'),
      render: (r) => `${new Date(r.billingPeriodStart).toLocaleDateString()} – ${new Date(r.billingPeriodEnd).toLocaleDateString()}`,
    },
    { field: 'totalAmount', headerName: t('invoicesPage.table.totalAmount'), align: 'right', render: (r) => formatCurrency(r.totalAmount) },
    { field: 'status', headerName: t('invoicesPage.table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      render: (r) =>
        r.pdfPath ? (
          <Button size="small" startIcon={<DownloadIcon />} onClick={() => void downloadInvoicePdf(r)}>
            {t('invoicesPage.table.downloadPdf')}
          </Button>
        ) : null,
    },
  ];

  return (
    <Stack spacing={3}>
      <GridContainer columns={12}>
        <GridItem xs={12} sm={4}>
          <StatCard label={t('invoicesPanel.totalInvoiced')} value={formatCurrency(totalInvoiced)} icon={<ReceiptLongIcon />} accentColor={colors.invoices} />
        </GridItem>
        <GridItem xs={12} sm={4}>
          <StatCard label={t('invoicesPanel.paidAmount')} value={formatCurrency(paidAmount)} icon={<PaidOutlinedIcon />} accentColor={colors.overview} />
        </GridItem>
        <GridItem xs={12} sm={4}>
          <StatCard label={t('invoicesPanel.unpaidAmount')} value={formatCurrency(unpaidAmount)} icon={<PendingActionsIcon />} accentColor={colors.dispatch} />
        </GridItem>
      </GridContainer>

      <GridContainer columns={12} spacing={2}>
        <GridItem xs={12} md={7}>
          <ChartCard title={t('invoicesPanel.trendTitle')} loading={isLoading} empty={!isLoading && monthlyBilled.length === 0}>
            <DashboardLineChart
              categories={monthlyBilled.map((m) => m.month)}
              series={[{ label: t('invoicesPanel.billedAmount'), data: monthlyBilled.map((m) => m.values.amount), color: colors.invoices }]}
              legendPosition="bottom"
              showValueLabels
            />
          </ChartCard>
        </GridItem>
        <GridItem xs={12} md={5}>
          <ChartCard title={t('invoicesPanel.statusTitle')} loading={isLoading} empty={!isLoading && statusDistribution.length === 0}>
            <DashboardPieChart
              data={statusDistribution.map((s, i) => ({ label: s.key, value: s.count, color: STATUS_SLICE_COLORS[i % STATUS_SLICE_COLORS.length] }))}
              legendPosition="bottom"
            />
          </ChartCard>
        </GridItem>
      </GridContainer>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          {t('invoicesPage.title')}
        </Typography>
        <PortalDateRangeFilter fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate} />
        <DataTable columns={columns} rows={tableRows} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="invoices" />
      </Paper>
    </Stack>
  );
}
